import { countBy, keys, map, uniq, uniqBy } from "lodash";
import { useAnalyticsModalContext } from "PFApp/analytics/parts/top_actions_panel/analytics_modal_context_provider";
import { getCategoryTitle } from "PFApp/analytics/parts/top_actions_panel/export_wizard_content/export_fields_utils";
import { ExportField, ExportFields } from "PFApp/analytics/types/export_analytics";
import { Accordion } from "PFComponents/accordion/accordion";
import Toggle from "PFComponents/toggle/toggle";
import React, { JSX, useCallback, useMemo, useState } from "react";

import css from "./field_category.module.scss";
import { FieldCategoryItems } from "./field_category_items";

interface FieldToggleProps {
  allExpanded: boolean;
  categoryKey: string;
  fields: ExportFields;
}

export const FieldCategory = ({ allExpanded, categoryKey, fields }: FieldToggleProps): JSX.Element => {
  const {
    updateData,
    state: {
      data: { selectedFields }
    }
  } = useAnalyticsModalContext();

  const [expanded, setExpanded] = useState<boolean>(false);

  const areAllSelected = useMemo(() => {
    const selectedFieldsCount = countBy(selectedFields, (field) => field.id?.includes(categoryKey)).true;
    return keys(fields).length === selectedFieldsCount;
  }, [categoryKey, fields, selectedFields]);

  const onFieldToggleProvider = useCallback(
    (selectedField: ExportField) => (checked: boolean) => {
      const newSelectedFields = checked
        ? uniq([...selectedFields, selectedField])
        : selectedFields.filter((field) => field.id !== selectedField.id);
      updateData({ selectedFields: newSelectedFields });
    },
    [selectedFields, updateData]
  );

  const handleToggleAll = () => {
    if (areAllSelected) {
      updateData({ selectedFields: selectedFields.filter((field) => !field.id?.includes(categoryKey)) });
    } else {
      const newFields = map(fields, (item, fieldKey) => ({ ...item, id: `${categoryKey}-${fieldKey}` }));
      updateData({ selectedFields: uniqBy([...selectedFields, ...newFields], "id") });
    }
  };

  return (
    <div className={css.root}>
      <Accordion
        header={getCategoryTitle(categoryKey)}
        state={expanded || allExpanded ? "open" : "closed"}
        onStateChange={() => setExpanded((prev) => !prev)}
        headerSize="small"
        actions={<Toggle onChange={handleToggleAll} checked={areAllSelected} />}
      >
        <FieldCategoryItems
          categoryKey={categoryKey}
          fields={fields}
          onFieldToggleProvider={onFieldToggleProvider}
        />
      </Accordion>
    </div>
  );
};
